import { Fragment, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { useFormatting } from '@/admin/hooks/use-formatting'
import {
  type AvailabilityRule,
  type PolicyGroupProductSummary,
  type PolicyGroupSummary,
  useBulkDeleteAvailabilityRules,
  useCreateAvailabilityRule,
  usePolicyGroupsPage,
} from '@/admin/hooks/use-availability-rules'
import {
  flattenProductCategories,
  useProductCategories,
  useProductTemplates,
} from '@/admin/hooks/use-products'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { SearchInput } from '@/shared/components/ui/search-input'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'
import PolicyMappingDialog from '@/admin/pages/manage/warranty-policies/policy-mapping-dialog'
import {
  makePolicyRuleCloneInput,
  suggestNextYearPolicyCode,
} from '@/admin/pages/manage/warranty-policies/policy-utils'
import { warrantyTypeLabel } from '@/admin/pages/transfers/components/warranty-label'

const PAGE_SIZE = 10

export function WarrantyPoliciesPage() {
  const { formatDate } = useFormatting()
  const navigate = useNavigate()
  const formatPolicyDate = (value: string | null): string =>
    value ? formatDate(value) : '-'
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1)
  const searchQuery = searchParams.get('q') ?? ''
  const expandedParam = searchParams.get('expand') ?? ''
  const expandedCodes = useMemo(
    () => new Set(expandedParam.split(',').filter(Boolean)),
    [expandedParam],
  )

  const { data: groupsPage, isLoading } = usePolicyGroupsPage({
    page,
    page_size: PAGE_SIZE,
    search: searchQuery.trim() || undefined,
  })
  const groups = useMemo(() => groupsPage?.results ?? [], [groupsPage])
  const totalCount = groupsPage?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)

  const createRule = useCreateAvailabilityRule()
  const bulkDeleteRules = useBulkDeleteAvailabilityRules()
  const { data: productTemplates = [] } = useProductTemplates()
  const { data: productCategoriesTree = [] } = useProductCategories()
  const productCategories = useMemo(
    () => flattenProductCategories(productCategoriesTree),
    [productCategoriesTree],
  )

  const [dialogState, setDialogState] = useState<{
    open: boolean
    mode: 'create-policy' | 'add-mapping' | 'edit-mapping'
    policyCode?: string
    editing: AvailabilityRule | null
    sourceRule: AvailabilityRule | null
  }>({
    open: false,
    mode: 'create-policy',
    policyCode: undefined,
    editing: null,
    sourceRule: null,
  })

  const [removalState, setRemovalState] = useState<{
    group: PolicyGroupSummary
    product: PolicyGroupProductSummary
    rules: AvailabilityRule[]
  } | null>(null)

  const [groupDeletion, setGroupDeletion] = useState<PolicyGroupSummary | null>(
    null,
  )

  const [duplicateState, setDuplicateState] = useState<{
    open: boolean
    action: 'clone' | 'next-year'
    group: PolicyGroupSummary | null
    nextPolicyCode: string
  }>({
    open: false,
    action: 'clone',
    group: null,
    nextPolicyCode: '',
  })

  const availableAttributes = useMemo(() => {
    const seen = new Set<string>()
    return productTemplates.flatMap((template) =>
      (template.attributes ?? []).filter((attribute) => {
        if (seen.has(attribute.code_name)) return false
        seen.add(attribute.code_name)
        return true
      }),
    )
  }, [productTemplates])

  const existingPolicyCodes = useMemo(
    () => groups.map((group) => group.policy_code),
    [groups],
  )

  function updateUrlParams(next: Record<string, string | null>) {
    const merged = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(next)) {
      if (!value) merged.delete(key)
      else merged.set(key, value)
    }
    setSearchParams(merged, { replace: true })
  }

  function handlePageChange(nextPage: number) {
    updateUrlParams({ page: nextPage <= 1 ? null : String(nextPage) })
  }

  function toggleExpanded(policyCode: string) {
    const next = new Set(expandedCodes)
    if (next.has(policyCode)) next.delete(policyCode)
    else next.add(policyCode)
    updateUrlParams({ expand: next.size > 0 ? [...next].join(',') : null })
  }

  function openCreatePolicy() {
    setDialogState({
      open: true,
      mode: 'create-policy',
      policyCode: undefined,
      editing: null,
      sourceRule: null,
    })
  }

  function openAddMapping(group: PolicyGroupSummary) {
    const sourceRule =
      group.rules.find((rule) => rule.scope === 'warranty_filter') ?? group.rules[0] ?? null
    if (!sourceRule) {
      toast.error('No source rule found for this policy.')
      return
    }
    setDialogState({
      open: true,
      mode: 'add-mapping',
      policyCode: group.policy_code,
      editing: null,
      sourceRule,
    })
  }

  function openDuplicateDialog(
    action: 'clone' | 'next-year',
    group: PolicyGroupSummary,
  ) {
    setDuplicateState({
      open: true,
      action,
      group,
      nextPolicyCode:
        action === 'next-year' ? suggestNextYearPolicyCode(group.policy_code) : '',
    })
  }

  function startProductRemoval(
    group: PolicyGroupSummary,
    product: PolicyGroupProductSummary,
  ) {
    const rulesForProduct = group.rules.filter(
      (rule) => rule.scope_id === product.uuid,
    )
    if (rulesForProduct.length === 0) {
      toast.error('No mapping found for this product.')
      return
    }
    setRemovalState({ group, product, rules: rulesForProduct })
  }

  async function confirmRemoveProduct() {
    if (!removalState) return
    try {
      await bulkDeleteRules.mutateAsync(
        removalState.rules.map((rule) => rule.uuid),
      )
      toast.success(
        removalState.rules.length === 1
          ? 'Product mapping removed'
          : `${removalState.rules.length} product mappings removed`,
      )
      setRemovalState(null)
    } catch (err) {
      toastApiError(err, 'Failed to remove product mapping')
    }
  }

  async function confirmDeleteGroup() {
    if (!groupDeletion) return
    try {
      await bulkDeleteRules.mutateAsync(groupDeletion.rules.map((rule) => rule.uuid))
      toast.success(`Policy ${groupDeletion.policy_code} deleted`)
      setGroupDeletion(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete policy')
    }
  }

  async function confirmDuplicatePolicy() {
    const nextPolicyCode = duplicateState.nextPolicyCode.trim().toUpperCase()
    if (!duplicateState.group) return
    if (!nextPolicyCode) {
      toast.error('Policy number is required')
      return
    }
    if (
      existingPolicyCodes.some(
        (value) => value.trim().toLowerCase() === nextPolicyCode.toLowerCase(),
      )
    ) {
      toast.error('Policy number must be unique')
      return
    }

    try {
      for (const rule of duplicateState.group.rules) {
        await createRule.mutateAsync(makePolicyRuleCloneInput(rule, nextPolicyCode))
      }
      toast.success(
        duplicateState.action === 'next-year'
          ? `Policy ${nextPolicyCode} created for next year`
          : `Policy ${nextPolicyCode} cloned`,
      )
      setDuplicateState({
        open: false,
        action: 'clone',
        group: null,
        nextPolicyCode: '',
      })
    } catch (err) {
      toastApiError(err, 'Failed to duplicate policy')
    }
  }

  const columns: DataTableColumn<PolicyGroupSummary>[] = [
    {
      id: 'policy_code',
      header: 'Policy Number',
      cell: (group) => (
        <span className="font-medium text-[var(--ink)]">
          {group.policy_code}
        </span>
      ),
    },
    {
      id: 'warranty_type',
      header: 'Warranty Type',
      cell: (group) => (
        <div className="flex flex-col gap-0.5">
          <span>
            {group.warranty_types.length > 0
              ? Array.from(
                  new Set(group.warranty_types.map(warrantyTypeLabel)),
                ).join(', ')
              : '-'}
          </span>
          <span className="text-[11px] text-[var(--ink-3)]">
            {group.warranty_templates.length > 0
              ? group.warranty_templates.map((t) => t.name).join(', ')
              : 'No template'}
          </span>
        </div>
      ),
    },
    {
      id: 'start_date',
      header: 'Start Date',
      cell: (group) => formatPolicyDate(group.start_date),
    },
    {
      id: 'end_date',
      header: 'End Date',
      cell: (group) => formatPolicyDate(group.end_date),
    },
    {
      id: 'mapped_products',
      header: 'Mapped Products',
      cell: (group) => (
        <span className="tabular-nums text-[var(--ink-2)]">
          {group.mapped_products.length}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (group) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              openDuplicateDialog('clone', group)
            }}
            className="h-8 px-2 text-[11px]"
          >
            <Copy className="mr-1 h-3 w-3" />
            Clone
          </Button>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              openDuplicateDialog('next-year', group)
            }}
            className="h-8 px-2 text-[11px]"
          >
            New year
          </Button>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              setGroupDeletion(group)
            }}
            className="h-8 px-2 text-[11px] text-[var(--status-error)] hover:text-[var(--status-error)]"
          >
            <Trash2 className="mr-1 h-3 w-3" />
            Delete
          </Button>
        </div>
      ),
    },
  ]

  const hasPolicies = totalCount > 0 || (!isLoading && searchQuery.trim() === '')

  return (
    <div className="py-6" data-testid="warranty-policies">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <span className="clara-section-label">
            Policies
            {searchQuery.trim() && (
              <span className="ml-2 normal-case tracking-normal text-[var(--ink-3)]">
                ({totalCount} match{totalCount === 1 ? '' : 'es'})
              </span>
            )}
          </span>
          <p className="mt-2 max-w-[680px] text-[12px] text-[var(--ink-3)]">
            Policies group the coverage rule and any product mappings that share
            a policy number, ordered by the latest availability window. Expand
            a row to manage the mapped products.
          </p>
        </div>
        <Button
          type="button"
          onClick={openCreatePolicy}
          className="flex h-auto cursor-pointer items-center gap-1.5 rounded-[4px] bg-[var(--green)] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)]"
        >
          <Plus className="h-3.5 w-3.5" />
          New policy
        </Button>
      </div>

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={(value) =>
            updateUrlParams({
              q: value?.trim() ? value : null,
              page: null,
            })
          }
          placeholder="Search policy number..."
          iconSize={12}
          className="h-8 text-[12px]"
        />
      </div>

      <DataTable<PolicyGroupSummary>
        tableId="warranty-policies"
        columns={columns}
        data={groups}
        isLoading={isLoading}
        emptyStateNode={
          <EmptyState
            icon={ShieldCheck}
            title={
              hasPolicies && searchQuery.trim()
                ? 'No policies match your search'
                : 'No policies yet'
            }
            description={
              hasPolicies && searchQuery.trim()
                ? 'Try a different policy number.'
                : 'Create a policy, then add one or more product mappings when you are ready.'
            }
            action={
              hasPolicies && searchQuery.trim()
                ? undefined
                : { label: 'New policy', onClick: openCreatePolicy }
            }
          />
        }
        rowKey={(group) => group.policy_code}
        renderRow={(group, visibleColumns) => {
          const isExpanded = expandedCodes.has(group.policy_code)
          return (
            <Fragment key={group.policy_code}>
              <tr
                className={cn(
                  'cursor-pointer border-b-[0.5px] border-b-[var(--rule)] transition-colors duration-100 hover:bg-[var(--bg-subtle)]',
                  isExpanded && 'bg-[var(--bg-subtle)]/40',
                )}
                onClick={() =>
                  navigate(
                    `/manage/warranties/policies/${encodeURIComponent(group.policy_code)}`,
                  )
                }
                data-testid={`policy-row-${group.policy_code}`}
              >
                {visibleColumns.map((col, index) => (
                  <td
                    key={col.id}
                    className="cv-data-table-body px-4 py-3 text-[var(--ink)]"
                  >
                    {index === 0 ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          onClick={(event) => {
                            event.stopPropagation()
                            toggleExpanded(group.policy_code)
                          }}
                          className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-[3px] text-[var(--ink-3)] transition-all duration-100 hover:bg-[var(--bg-subtle)]"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                        </button>
                        {col.cell ? col.cell(group) : null}
                      </div>
                    ) : col.cell ? (
                      col.cell(group)
                    ) : null}
                  </td>
                ))}
              </tr>
              {isExpanded ? (
                <tr className="border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg-subtle)]/30">
                  <td colSpan={visibleColumns.length} className="p-0">
                    <div className="w-0 min-w-full overflow-hidden px-4 py-4">
                    <div className="flex flex-col gap-3 pl-6">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
                          Mapped products ({group.mapped_products.length})
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            openAddMapping(group)
                          }}
                          className="h-7 px-2 text-[11px]"
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add product
                        </Button>
                      </div>
                      {group.mapped_products.length === 0 ? (
                        <p className="text-[12px] text-[var(--ink-3)]">
                          No products mapped to this policy yet.
                        </p>
                      ) : (
                        <ul className="flex flex-wrap gap-1.5">
                          {group.mapped_products.map((product) => (
                            <li
                              key={product.uuid}
                              className="group inline-flex items-center gap-1 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] py-1 pl-2.5 pr-1 text-[12px] text-[var(--ink)] transition-colors duration-150 hover:border-[var(--rule-strong)]"
                            >
                              <span className="truncate">
                                {product.cached_short_name}
                              </span>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  startProductRemoval(group, product)
                                }}
                                aria-label={`Remove ${product.cached_short_name}`}
                                className="flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-[3px] text-[var(--ink-3)] transition-colors duration-150 hover:bg-[var(--status-error)]/10 hover:text-[var(--status-error)]"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    </div>
                  </td>
                </tr>
              ) : null}
            </Fragment>
          )
        }}
      />

      {totalCount > 0 ? (
        <div className="cv-data-table mt-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="cv-data-table-small text-[var(--ink-3)]">
              Page {safePage} of {totalPages}
            </span>
            <span className="cv-data-table-small text-[var(--ink-3)]">
              {PAGE_SIZE} policies per page · {totalCount} total
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              onClick={() => handlePageChange(Math.max(1, safePage - 1))}
              disabled={safePage <= 1}
              className="cv-data-table-small cursor-pointer rounded-[4px] px-3 py-1.5 text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)] disabled:cursor-default disabled:opacity-30"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => handlePageChange(Math.min(totalPages, safePage + 1))}
              disabled={safePage >= totalPages}
              className="cv-data-table-small cursor-pointer rounded-[4px] px-3 py-1.5 text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)] disabled:cursor-default disabled:opacity-30"
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <PolicyMappingDialog
        attributes={availableAttributes}
        productTemplates={productTemplates}
        productCategories={productCategories}
        open={dialogState.open}
        onOpenChange={(open) => {
          setDialogState((prev) => ({ ...prev, open }))
          if (!open) {
            setDialogState({
              open: false,
              mode: 'create-policy',
              policyCode: undefined,
              editing: null,
              sourceRule: null,
            })
          }
        }}
        editing={dialogState.editing}
        sourceRule={dialogState.sourceRule}
        mode={dialogState.mode}
        policyCode={dialogState.policyCode}
        existingPolicyCodes={existingPolicyCodes}
      />

      <ConfirmDeleteDialog
        open={!!removalState}
        onOpenChange={(open) => {
          if (!open) setRemovalState(null)
        }}
        title="Remove product mapping?"
        description={
          removalState ? (
            <>
              Remove <strong>{removalState.product.cached_short_name}</strong> from
              policy <strong>{removalState.group.policy_code}</strong>? This will
              delete {removalState.rules.length} rule
              {removalState.rules.length === 1 ? '' : 's'}.
            </>
          ) : null
        }
        onConfirm={confirmRemoveProduct}
        isPending={bulkDeleteRules.isPending}
      />

      <ConfirmDeleteDialog
        open={!!groupDeletion}
        onOpenChange={(open) => {
          if (!open) setGroupDeletion(null)
        }}
        title="Delete policy?"
        description={
          groupDeletion ? (
            <>
              Delete policy <strong>{groupDeletion.policy_code}</strong> along
              with all {groupDeletion.rules.length} of its product mappings? This
              action cannot be undone.
            </>
          ) : null
        }
        onConfirm={confirmDeleteGroup}
        isPending={bulkDeleteRules.isPending}
      />

      <Dialog
        open={duplicateState.open}
        onOpenChange={(open) =>
          setDuplicateState((prev) => ({
            ...prev,
            open,
            group: open ? prev.group : null,
            nextPolicyCode: open ? prev.nextPolicyCode : '',
          }))
        }
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {duplicateState.action === 'next-year'
                ? 'New policy for next year'
                : 'Clone policy'}
            </DialogTitle>
            <DialogDescription>
              {duplicateState.group
                ? `Duplicates ${duplicateState.group.rules.length} mapping${duplicateState.group.rules.length === 1 ? '' : 's'} from policy ${duplicateState.group.policy_code}.`
                : 'Duplicate the selected policy.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="next-policy-code">New policy number</Label>
            <Input
              id="next-policy-code"
              value={duplicateState.nextPolicyCode}
              onChange={(event) =>
                setDuplicateState((prev) => ({
                  ...prev,
                  nextPolicyCode: event.target.value.toUpperCase(),
                }))
              }
              placeholder="Enter a unique policy number"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                setDuplicateState({
                  open: false,
                  action: 'clone',
                  group: null,
                  nextPolicyCode: '',
                })
              }
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmDuplicatePolicy}
              disabled={createRule.isPending}
            >
              {duplicateState.action === 'next-year' ? 'Create next year' : 'Clone'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default WarrantyPoliciesPage
